import { getExerciseImageUrl } from "site/services/content-base-url";
import type { Step } from "site/services/app.service";

const preloadedImages = new Map<string, Promise<void>>();

function preloadImage(url: string): Promise<void> {
  const cached = preloadedImages.get(url);
  if (cached) {
    return cached;
  }

  const promise = new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
  preloadedImages.set(url, promise);
  return promise;
}

export function preloadExerciseImages(urls: Iterable<string | null | undefined>): Promise<void> {
  const unique = [...new Set([...urls].filter((url): url is string => !!url))];
  if (!unique.length) {
    return Promise.resolve();
  }

  return Promise.all(unique.map((url) => preloadImage(url))).then(() => undefined);
}

export function collectStepImageUrls(
  step: Step | null | undefined,
  exerciseImages: Record<string, string> | null | undefined,
): string[] {
  if (!step?.descriptor) {
    return [];
  }

  const resolveImage = (id: number | string | null | undefined): string | null => {
    if (id == null) {
      return null;
    }
    const path = exerciseImages?.[`${id}`];
    return path ? getExerciseImageUrl(path) : null;
  };

  const subIndex = step.subIndex;
  const chunk = step.data?.[subIndex];
  const stepKey = step.descriptor.key;
  const urls: string[] = [];

  if (stepKey === "groups" || stepKey === "dialogs" || stepKey === "dictionaries") {
    const rows = Array.isArray(chunk) ? chunk : [];
    for (const row of rows) {
      const url = resolveImage(row?.id);
      if (url) {
        urls.push(url);
      }
    }
    return urls;
  }

  if (stepKey === "single" || stepKey === "select" || stepKey === "sentences") {
    const url = resolveImage(chunk?.id);
    return url ? [url] : [];
  }

  return urls;
}

export function scheduleExerciseQuestionImage(
  questionId: number | string | null | undefined,
  imageUrl: string | null | undefined,
  isCurrent: () => boolean,
  setImage: (url: string | null) => void,
  getCurrent?: () => string | null,
): void {
  const current = getCurrent?.() ?? null;
  if (!imageUrl) {
    if (current) {
      setImage(null);
    }
    return;
  }

  if (imageUrl === current) {
    return;
  }

  void preloadExerciseImages([imageUrl]).then(() => {
    if (isCurrent()) {
      setImage(imageUrl);
    }
  });
}
